import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "@radix-ui/react-icons";
import { LuUser, LuUsers } from "react-icons/lu";
import {
  OrganizationPublicResponse,
  OrganizationWebPagePublicResponse,
  TutorPublicResponse,
} from "client/openapi";
import { inEmbed } from "util/embed";
import PublicClasses from "./PublicClasses";
import { OrgLogo, PersonPhoto, PublicShell } from "./PublicShell";
import {
  classesHref,
  orgHref,
  phoneLabel,
  tint,
  tutorHref,
} from "./publicBooking";

export const tutorSubjects = (tutor: TutorPublicResponse) =>
  tutor.subjects.filter((s) => s.active).map((s) => s.org_subject);

/** The org's contact details on one line. */
export function ContactLine({ org }: { org: OrganizationPublicResponse }) {
  const parts = [
    org.public_phone_number && phoneLabel(org.public_phone_number),
    org.public_email,
    [org.address_line_1, [org.city, org.state].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);
  if (!parts.length) return null;
  return (
    <p className="flex flex-wrap justify-center gap-x-5 gap-y-1 px-6 pb-7 text-sm text-gray-500">
      {parts.map((part, i) => (
        <span key={i}>{part}</span>
      ))}
    </p>
  );
}

/**
 * The org page: logo, the admin's title and about; the tutors, filtered by subject in
 * one click, or the classes open for enrollment; the contact line. In a
 * widget on the org's own website, only what is under the tabs: the
 * website shows its own name and contact details.
 */
export default function PublicOrgPage({
  page,
  org,
  tab = "tutors",
}: {
  page: OrganizationWebPagePublicResponse;
  org: OrganizationPublicResponse;
  tab?: "tutors" | "classes";
}) {
  const accent = page.primary_color;
  const [subjectId, setSubjectId] = useState<number>();

  // Chips for the subjects at least one listed tutor teaches.
  const subjects = useMemo(() => {
    const taught = new Set(
      org.tutors.flatMap((t) => tutorSubjects(t).map((s) => s.id))
    );
    return org.subjects
      .filter((s) => taught.has(s.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [org]);

  const tutors = org.tutors
    .filter(
      (t) =>
        subjectId === undefined ||
        tutorSubjects(t).some((s) => s.id === subjectId)
    )
    .sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.last_name}`
      )
    );

  // Both tabs always show, so moving between them never hides one; the
  // Classes tab says so when no class is open.
  const tabs = [
    {
      value: "tutors",
      label: "Tutors",
      icon: <LuUser />,
      count: org.tutors.length,
      href: orgHref(org.id),
    },
    {
      value: "classes",
      label: "Classes",
      icon: <LuUsers />,
      count: page.booking.open_class_count ?? 0,
      href: classesHref(org.id),
    },
  ];

  const chip = (selected: boolean) =>
    selected
      ? { backgroundColor: accent, borderColor: accent, color: "white" }
      : undefined;

  const embed = inEmbed();

  return (
    <PublicShell width="narrow">
      {!embed && (
        <div className="px-5 pt-8 text-center sm:px-9 sm:text-left">
          <div className="flex justify-center sm:justify-start">
            <OrgLogo
              url={page.photo_url}
              name={org.name}
              size={64}
              maxWidth={280}
              accent={accent}
            />
          </div>
          {page.title && (
            <h1 className="mt-4 text-[26px] font-bold leading-tight">
              {page.title}
            </h1>
          )}
          {page.bio && (
            <p
              className={`${
                page.title ? "mt-2" : "mt-4"
              } max-w-2xl whitespace-pre-line text-[15px] leading-relaxed text-gray-600 sm:mx-0`}
            >
              {page.bio}
            </p>
          )}

          <nav className="mt-7 flex justify-center gap-8 border-b border-gray-200 sm:justify-start">
            {tabs.map((t) => (
              <Link
                key={t.value}
                to={t.href}
                aria-current={tab === t.value ? "page" : undefined}
                className={`-mb-px flex items-center gap-2 border-b-[3px] pb-3 text-[15px] font-semibold ${
                  tab === t.value
                    ? "text-neutral-900"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                style={tab === t.value ? { borderColor: accent } : undefined}
              >
                {t.icon}
                {t.label}
                <span className="rounded-full bg-gray-100 px-2 text-xs font-medium text-gray-600">
                  {t.count}
                </span>
              </Link>
            ))}
          </nav>
        </div>
      )}

      {tab === "classes" ? (
        <PublicClasses page={page} org={org} />
      ) : org.tutors.length === 0 ? (
        <p className="px-9 py-12 text-center text-gray-600">
          No tutors are taking bookings online right now.
        </p>
      ) : (
        <>
          {subjects.length > 1 && (
            <div className="flex gap-2 overflow-x-auto px-5 pt-5 pb-1 sm:flex-wrap sm:px-9">
              <button
                type="button"
                onClick={() => setSubjectId(undefined)}
                className="shrink-0 rounded-full border border-gray-300 px-4 py-1.5 text-sm"
                style={chip(subjectId === undefined)}
              >
                All subjects
              </button>
              {subjects.map((subject) => (
                <button
                  key={subject.id}
                  type="button"
                  onClick={() => setSubjectId(subject.id)}
                  className="shrink-0 rounded-full border border-gray-300 px-4 py-1.5 text-sm"
                  style={chip(subjectId === subject.id)}
                >
                  {subject.name}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 px-5 pt-4 pb-6 sm:grid-cols-2 sm:px-9">
            {tutors.map((tutor) => {
              const name = `${tutor.first_name} ${tutor.last_name}`;
              return (
                <Link
                  key={tutor.id}
                  to={tutorHref(tutor, org.tutors, subjectId)}
                  className="flex items-center gap-4 rounded-xl border border-gray-200 p-5 transition hover:border-gray-300 hover:shadow-sm"
                >
                  <PersonPhoto
                    url={tutor.tutor_web_page?.photo_url}
                    name={name}
                    size={64}
                    accent={accent}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{name}</p>
                    <p className="truncate text-sm text-gray-500">
                      {tutorSubjects(tutor)
                        .map((s) => s.name)
                        .join(" · ")}
                    </p>
                    {tutor.tutor_web_page?.bio && (
                      <p className="mt-1.5 text-sm leading-snug text-gray-600 line-clamp-2">
                        {tutor.tutor_web_page.bio}
                      </p>
                    )}
                  </div>
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: tint(accent), color: accent }}
                    aria-hidden
                  >
                    <ChevronRightIcon />
                  </span>
                </Link>
              );
            })}
          </div>
        </>
      )}

      {!embed && <ContactLine org={org} />}
    </PublicShell>
  );
}
